import React, { useMemo, useState, useEffect } from "react";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { IconButton, Tooltip, Button, Box } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import Title from "../../../../../components/Title";
import Back from "../../../../../components/Back";
import type { CatalogProcedure as Catalog } from "../../../../../interfaces";
import { useCatalogProcedure } from "../../../../../api/catalog_procedure/use-catalog-procedure";
import { useNavigate } from "react-router-dom";
import FormattedDateCell from "../../../../../components/FormattedDateCell";

const CatalogProcedure = () => {
  const {
    data: catalogData = [],
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useCatalogProcedure();

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedCatalog, setSelectedCatalog] = useState<Catalog | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection).filter(
      (id) => rowSelection[id]
    );
    if (selectedIds.length === 1) {
      const catalogItem = catalogData.find(
        (item) => String(item.id) === selectedIds[0]
      );
      setSelectedCatalog(catalogItem || null);
    } else {
      setSelectedCatalog(null);
    }
  }, [rowSelection, catalogData]);
  const defaultCol = {
    minSize: 40,
    // maxSize: 1000,
    size: 50,
    // grow: true,
  };
  const columns = useMemo<MRT_ColumnDef<Catalog>[]>(
    () => [
      { accessorKey: "id", header: "ID" },
      {
        accessorKey: "title",
        header: "Title",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>()}>
            <span>{cell.getValue<string>()}</span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        Cell: ({ cell }) => <span>{cell.getValue<string>()}</span>,
      },
      { accessorKey: "version", header: "Version" },
      {
        accessorKey: "created_at",
        header: "Created At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        Cell: ({ cell }) => <FormattedDateCell value={cell.getValue<Date>()} />,
      },
    ],
    []
  );

  const table = useMemo(
    () => (
      <MaterialReactTable
        columns={columns}
        data={catalogData}
        defaultColumn={defaultCol}
        enableMultiRowSelection={false}
        enableRowSelection
        state={{
          rowSelection,
          isLoading,
          showAlertBanner: !!error,
          showProgressBars: isRefetching,
        }}
        onRowSelectionChange={(newSelection) => {
          const selectedKeys = Object.keys(newSelection).filter(
            (key) => newSelection[key]
          );
          if (selectedKeys.length > 1) {
            const lastKey = selectedKeys[selectedKeys.length - 1];
            setRowSelection({ [lastKey]: true });
          } else {
            setRowSelection(newSelection);
          }
        }}
        muiTableContainerProps={{ sx: { maxHeight: "calc(78vh - 112px)" } }}
        muiPaginationProps={{
          color: "secondary",
          rowsPerPageOptions: [10, 25, 50, 100],
          shape: "rounded",
          variant: "outlined",
        }}
        paginationDisplayMode="pages"
        columnFilterDisplayMode="popover"
        initialState={{
          pagination: { pageSize: 25, pageIndex: 0 },
          density: "compact",
          showGlobalFilter: true,
          sorting: [{ id: "created_at", desc: false }],
        }}
        enableColumnResizing
        enableGrouping={false}
        enableRowActions={false}
        enableColumnOrdering={false}
        enableColumnPinning
        enableStickyHeader
        enableColumnFilterModes
        positionActionsColumn="last"
        enableFacetedValues
        getRowId={(row) => String(row.id)}
        layoutMode="grid"
        muiToolbarAlertBannerProps={
          error ? { color: "error", children: "Error loading data" } : undefined
        }
        renderTopToolbarCustomActions={() => (
          <Box>
            <Button
              variant="contained"
              onClick={handleActivateProcedure}
              disabled={!selectedCatalog}
            >
              Select Agents
            </Button>
            <Tooltip arrow title="Refresh Data">
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />
    ),
    [
      catalogData,
      columns,
      error,
      isLoading,
      isRefetching,
      rowSelection,
      refetch,
      selectedCatalog,
    ]
  );

  const handleActivateProcedure = () => {
    if (selectedCatalog) {
      navigate(`/management/procedures/select-agent`, {
        state: { catalog: selectedCatalog },
      });
    }
  };

  return (
    <>
      <Title>Catalog Procedure</Title>
      {error && <div>Error loading data</div>}
      {table}
      <Back />
    </>
  );
};

export default CatalogProcedure;
